import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMedia, serializeDate, hydrateDate } from '../../contexts/MediaContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import {
  Video, Play, Clock, RefreshCw, Loader2, HardDrive,
  Gauge, FileVideo, Calendar as CalendarIcon, Camera, X,
  ChevronLeft, ChevronRight, ChevronDown, Search, MapPin, Info,
} from 'lucide-react';
import { VideoPlayerModal } from './VideoPlayerModal';
import { resolveTimezone, formatDT, formatWallDT, formatSpeed } from '../../utils/timezone';
import { getConfiguredCameraCount } from '../../utils/cameraRecordingState';
import { buildDevicesById, formatRecordDeviceLabel } from '../../utils/deviceLabel';
import { deviceDisplayName } from '../../utils/deviceName';

const pad = (n) => String(n).padStart(2, '0');
const dateKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const testIdToken = (value) => String(value ?? 'unknown').replace(/[^a-zA-Z0-9_-]+/g, '-');
const DEFAULT_TIME_FROM = '00:00:00';
const DEFAULT_TIME_TO = '23:59:59';
const normalizeTimeValue = (value, fallback) => {
  const match = String(value || '').match(/^(\d{2}:\d{2})(?::\d{2})?$/);
  return match ? `${match[1]}:00` : fallback;
};
const timePickerValue = (value, fallback) => normalizeTimeValue(value, fallback).slice(0, 5);
const setTimeFromPickerValue = (setter, value) => setter(normalizeTimeValue(value, DEFAULT_TIME_FROM));
const setTimeToPickerValue = (setter, value) => {
  if (value === '23:59') {
    setter(DEFAULT_TIME_TO);
    return;
  }
  const normalized = normalizeTimeValue(value, DEFAULT_TIME_TO);
  setter(`${normalized.slice(0, 5)}:59`);
};
const TIME_OPTIONS = Array.from({ length: 48 }, (_, idx) => {
  const hh = pad(Math.floor(idx / 2));
  const mm = idx % 2 ? '30' : '00';
  return `${hh}:${mm}`;
});

const buildMonthDays = (monthDate) => {
  const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  return Array.from({ length: 42 }, (_, idx) => {
    const d = new Date(start);
    d.setDate(start.getDate() + idx);
    return d;
  });
};

const eventDisplayTime = (event, device) => {
  const value = event?.event_time || event?.start_time || event?.created_at;
  return event?.event_time_is_wall_clock ? formatWallDT(value, { seconds: false }) : formatDT(value, { device });
};

const channelLabelFor = (device, channel) => {
  if (!channel) return 'Channel';
  return device?.channel_names?.[String(channel)] || `CH${channel}`;
};

const hasEventLocation = (event) => {
  const lat = Number(event?.latitude);
  const lng = Number(event?.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0);
};

const eventLabelText = (event) => String(event?.event_label_display || event?.event_label || event?.event_type || 'Event');

const stripEventCodeSuffix = (label) => String(label || '').replace(/\s*\(\d{1,5}\)\s*$/u, '').trim();

const eventLabelRows = (event) => {
  const explicitRows = Array.isArray(event?.event_labels_with_codes)
    ? event.event_labels_with_codes
    : [];
  const sourceRows = explicitRows.length > 0 ? explicitRows : eventLabelText(event).split(/\s+\+\s+/);
  return sourceRows
    .map(row => stripEventCodeSuffix(row))
    .filter(Boolean);
};

export const EventVideos = () => {
  const { api } = useAuth();
  // Direct Consume from page-level MediaContext so filter state survives sub-tab
  // switches (Event Videos ↔ Live Videos ↔ Remote Downloads) and page navigation.
  // The component unmounts whenever Media.jsx flips activeTab away from 'events'.
  const {
    evSelectedDeviceIds: selectedDeviceIds,
    setEvSelectedDeviceIds: setSelectedDeviceIds,
    evSelectedEventType: selectedEventType,
    setEvSelectedEventType: setSelectedEventType,
    evSelectedChannel: selectedChannel,
    setEvSelectedChannel: setSelectedChannel,
    evSelectedDateIso,
    setEvSelectedDateIso,
    evTimeFrom: timeFrom,
    setEvTimeFrom: setTimeFrom,
    evTimeTo: timeTo,
    setEvTimeTo: setTimeTo,
    evMonthDateIso,
    setEvMonthDateIso,
  } = useMedia();

  // Hydrate single Date objects from ISO strings on mount; persist back on change
  const [selectedDate, setSelectedDateLocal] = useState(() => hydrateDate(evSelectedDateIso, null));
  const [monthDate, setMonthDateLocal] = useState(() => hydrateDate(evMonthDateIso, new Date()));
  // Stable mirror setters (state setters from useState are guaranteed stable, so safe to omit from deps)
  const setSelectedDate = useCallback((d) => {
    setSelectedDateLocal(d);
    setEvSelectedDateIso(serializeDate(d));
  }, [setEvSelectedDateIso]);
  const setMonthDate = useCallback((d) => {
    setMonthDateLocal(d);
    setEvMonthDateIso(serializeDate(d));
  }, [setEvMonthDateIso]);

  // Deep-link from Event Detail modal / notification bell:
  // /media?tab=events&device_id=&date=YYYY-MM-DD&time=HH:MM:SS[&video_id=]
  // Reacts to URL changes (not just mount) so it also works when the user is
  // ALREADY on the Event Videos page when they click the deep link.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const deepDeviceId = params.get('device_id');
    const deepDate = params.get('date');
    if (!deepDeviceId) return;
    if (!deepDate) {
      // Device-only deep link (Vehicle Dashboard "View all"): filter to the vehicle, no date filter.
      setSelectedDeviceIds([deepDeviceId]);
      setDeepLinkFilterActive(true);
      navigate(`${location.pathname}?tab=events`, { replace: true });
      return;
    }
    const parsedDate = new Date(`${deepDate}T00:00:00`);
    if (Number.isNaN(parsedDate.getTime())) return;
    setSelectedDeviceIds([deepDeviceId]);
    setSelectedDate(parsedDate);
    setMonthDate(parsedDate);
    const timeMatch = String(params.get('time') || '').match(/^(\d{2}):(\d{2})/);
    if (timeMatch) {
      const mins = Number(timeMatch[1]) * 60 + Number(timeMatch[2]);
      const from = Math.max(0, mins - 2);
      const to = Math.min(23 * 60 + 59, mins + 2);
      setTimeFrom(`${pad(Math.floor(from / 60))}:${pad(from % 60)}:00`);
      setTimeTo(`${pad(Math.floor(to / 60))}:${pad(to % 60)}:59`);
    } else {
      setTimeFrom(DEFAULT_TIME_FROM);
      setTimeTo(DEFAULT_TIME_TO);
    }
    setDeepLinkFilterActive(true);
    navigate(`${location.pathname}?tab=events`, { replace: true });
    const deepVideoId = params.get('video_id');
    if (deepVideoId) {
      const ids = deepVideoId.split(',').filter(Boolean);
      (async () => {
        try {
          const results = await Promise.all(ids.map(id => api.get(`/media/event-videos/${id}`).catch(() => null)));
          const clips = results.map(r => r?.data).filter(v => v?.id);
          if (clips.length === 1) {
            await handleVideoClick(clips[0]);
          } else if (clips.length > 1) {
            setClipChoices(clips);
          }
        } catch (e) {
          console.error('Deep-link video open failed:', e);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [calendarDays, setCalendarDays] = useState({});
  const [deviceDropdownOpen, setDeviceDropdownOpen] = useState(false);
  const [deviceSearchQuery, setDeviceSearchQuery] = useState('');
  const [skip, setSkip] = useState(0);
  const limit = 24;
  const deviceSearchInputRef = useRef(null);
  const playbackPollTimersRef = useRef({});
  const eventRequestSeqRef = useRef(0);
  const calendarRequestSeqRef = useRef(0);

  useEffect(() => () => {
    Object.values(playbackPollTimersRef.current).forEach(clearTimeout);
    playbackPollTimersRef.current = {};
  }, []);

  // Phase 2C: live id→device map so event cards & the player modal render
  // current Asset/Unit after renames without a refetch.
  const devicesById = useMemo(() => buildDevicesById(devices), [devices]);

  // Video player
  const [playerOpen, setPlayerOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  // Multi-clip deep-link chooser (e.g. Impact event with Cabin + Front clips)
  const [clipChoices, setClipChoices] = useState(null);
  // True when a notification deep link narrowed the view (single device + short
  // time window). Drives the reminder banner so the user knows to Clear before
  // browsing other devices. Only cleared by clearFilters() — NOT on device
  // change, otherwise the hint would vanish while the time filter still hides results.
  const [deepLinkFilterActive, setDeepLinkFilterActive] = useState(false);

  const fetchDevices = useCallback(async () => {
    try {
      const res = await api.get('/media/video-devices');
      setDevices(res.data);
    } catch (e) {
      console.error('Failed to fetch video devices:', e);
    }
  }, [api]);

  const fetchEventTypes = useCallback(async () => {
    try {
      const res = await api.get('/media/event-video-types');
      setEventTypes(res.data);
    } catch (e) {
      console.error('Failed to fetch event types:', e);
    }
  }, [api]);

  const fetchEvents = useCallback(async () => {
    const requestSeq = eventRequestSeqRef.current + 1;
    eventRequestSeqRef.current = requestSeq;
    setLoading(true);
    try {
      if (selectedDeviceIds.length === 0) {
        if (requestSeq === eventRequestSeqRef.current) {
          setEvents([]);
          setTotal(0);
        }
        return;
      }
      const params = new URLSearchParams();
      params.append('device_ids', selectedDeviceIds.join(','));
      if (selectedEventType !== 'all') params.append('event_type', selectedEventType);
      if (selectedChannel !== 'all') params.append('channel', selectedChannel);
      if (selectedDate) {
        params.append('event_date', dateKey(selectedDate));
        params.append('time_from', timeFrom || DEFAULT_TIME_FROM);
        params.append('time_to', timeTo || DEFAULT_TIME_TO);
      }
      params.append('skip', skip.toString());
      params.append('limit', limit.toString());

      const res = await api.get(`/media/event-videos?${params.toString()}`);
      if (requestSeq !== eventRequestSeqRef.current) return;
      setEvents(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      if (requestSeq !== eventRequestSeqRef.current) return;
      console.error('Failed to fetch event videos:', e);
      setEvents([]);
      setTotal(0);
    } finally {
      if (requestSeq === eventRequestSeqRef.current) {
        setLoading(false);
      }
    }
  }, [api, selectedDeviceIds, selectedEventType, selectedChannel, selectedDate, timeFrom, timeTo, skip]);

  const fetchCalendar = useCallback(async () => {
    const requestSeq = calendarRequestSeqRef.current + 1;
    calendarRequestSeqRef.current = requestSeq;
    try {
      if (selectedDeviceIds.length === 0) {
        if (requestSeq === calendarRequestSeqRef.current) {
          setCalendarDays({});
        }
        return;
      }
      const params = new URLSearchParams({
        year: String(monthDate.getFullYear()),
        month: String(monthDate.getMonth() + 1),
      });
      params.append('device_ids', selectedDeviceIds.join(','));
      if (selectedEventType !== 'all') params.append('event_type', selectedEventType);
      if (selectedChannel !== 'all') params.append('channel', selectedChannel);
      const res = await api.get(`/media/event-videos/calendar?${params.toString()}`);
      if (requestSeq !== calendarRequestSeqRef.current) return;
      const mapped = {};
      (res.data?.days || []).forEach(day => { mapped[day.date] = day; });
      setCalendarDays(mapped);
    } catch (e) {
      if (requestSeq !== calendarRequestSeqRef.current) return;
      console.error('Failed to fetch event video calendar:', e);
      setCalendarDays({});
    }
  }, [api, monthDate, selectedDeviceIds, selectedEventType, selectedChannel]);

  useEffect(() => { fetchDevices(); fetchEventTypes(); }, [fetchDevices, fetchEventTypes]);
  useEffect(() => { setSkip(0); }, [selectedDeviceIds, selectedEventType, selectedChannel, selectedDate, timeFrom, timeTo]);
  useEffect(() => { fetchEvents(); }, [fetchEvents]);
  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  const deviceById = useMemo(() => Object.fromEntries(devices.map(d => [d.id, d])), [devices]);
  const selectedDeviceSet = useMemo(() => new Set(selectedDeviceIds), [selectedDeviceIds]);
  const selectedDevices = useMemo(
    () => selectedDeviceIds.map(id => deviceById[id]).filter(Boolean),
    [selectedDeviceIds, deviceById],
  );
  const selectedDeviceObj = selectedDevices.length === 1 ? selectedDevices[0] : null;
  const eventDevices = useMemo(() => {
    const map = new Map(devices.map(d => [d.id, d]));
    events.forEach(event => {
      if (!map.has(event.device_id)) {
        map.set(event.device_id, {
          id: event.device_id,
          name: event.device_name,
          channel_names: event.channel_names || {},
          total_cameras: event.total_cameras,
          connected_cameras: event.connected_cameras,
          timezone_override_enabled: event.timezone_override_enabled,
          timezone_override: event.timezone_override,
          timezone_auto: event.timezone_auto,
          effective_timezone: event.effective_timezone,
        });
      }
    });
    return [...map.values()].filter(d => d?.id);
  }, [devices, events]);

  const channelOptions = useMemo(() => {
    const sourceDevices = selectedDevices;
    const labels = new Map();
    sourceDevices.forEach(device => {
      const connected = Number(device?.connected_cameras || 0);
      const configured = connected > 0 ? connected : getConfiguredCameraCount(device);
      const namedCount = Object.keys(device?.channel_names || {}).length;
      const eventMax = events
        .filter(event => !device?.id || event.device_id === device.id)
        .reduce((max, event) => Math.max(max, Number(event.channel || 0)), 0);
      const count = Math.max(1, configured || namedCount || eventMax || 1);
      for (let ch = 1; ch <= count; ch += 1) {
        labels.set(ch, device?.channel_names?.[String(ch)] || labels.get(ch) || `CH${ch}`);
      }
    });
    return [...labels.entries()].sort(([a], [b]) => a - b).map(([id, label]) => ({ id, label }));
  }, [selectedDevices, events]);
  useEffect(() => {
    if (selectedChannel !== 'all' && !channelOptions.some(ch => String(ch.id) === String(selectedChannel))) {
      setSelectedChannel('all');
    }
  }, [selectedChannel, setSelectedChannel, channelOptions]);
  const filteredDevices = useMemo(() => {
    const q = deviceSearchQuery.trim().toLowerCase();
    return [...eventDevices]
      .sort((a, b) => deviceDisplayName(a).localeCompare(deviceDisplayName(b)))
      .filter(d => !q || deviceDisplayName(d).toLowerCase().includes(q) || String(d.name || '').toLowerCase().includes(q) || String(d.imei || '').toLowerCase().includes(q));
  }, [eventDevices, deviceSearchQuery]);
  const monthDays = useMemo(() => buildMonthDays(monthDate), [monthDate]);
  const selectedKey = selectedDate ? dateKey(selectedDate) : null;

  const allDevicesSelected = eventDevices.length > 0 && selectedDeviceIds.length === eventDevices.length;
  const selectionLabel = selectedDeviceIds.length === 0
    ? 'Select devices'
    : selectedDeviceIds.length === 1
      ? deviceDisplayName(selectedDevices[0]) || '1 device'
      : allDevicesSelected
        ? 'All devices'
        : `${selectedDeviceIds.length} devices`;

  const toggleAllDevices = () => {
    setSelectedDeviceIds(allDevicesSelected ? [] : eventDevices.map(d => d.id));
  };

  const toggleEventDevice = (id) => {
    setSelectedDeviceIds(prev => (
      prev.includes(id) ? prev.filter(existing => existing !== id) : [...prev, id]
    ));
  };

  const clearEventDeviceSelection = () => {
    setSelectedDeviceIds([]);
    setDeviceSearchQuery('');
    setDeviceDropdownOpen(false);
  };

  const pollEventPlaybackUrl = useCallback(async (video, attempt = 0) => {
    try {
      const res = await api.get(`/media/event-videos/${video.id}/play-url`);
      if (res.data?.url) {
        setVideoUrl(res.data.url);
        setSelectedVideo(prev => (prev ? {
          ...prev,
          playback_status: 'ready',
          playback_message: 'AVMSG ready',
          codec: res.data?.codec || prev.codec,
          browser_playback: res.data?.browser_playback || prev.browser_playback,
          requires_hevc_support: res.data?.requires_hevc_support ?? prev.requires_hevc_support,
          h264_fallback_ready: res.data?.h264_fallback_ready ?? prev.h264_fallback_ready,
          h264_fallback_status: res.data?.h264_fallback_status || prev.h264_fallback_status,
        } : prev));
        return;
      }
      const status = res.data?.playback_status || res.data?.status;
      const message = res.data?.message || 'Converting AVMSG for browser playback';
      if (['queued', 'converting'].includes(status) && attempt < 240) {
        setSelectedVideo(prev => (prev ? {
          ...prev,
          playback_status: 'converting',
          playback_message: message,
          playback_queue_position: res.data?.playback_queue_position,
        } : prev));
        if (attempt === 0) toast.info('AVMSG conversion queued. The video will open when ready.');
        const retryMs = Math.max(3, Number(res.data?.retry_after || 5)) * 1000;
        playbackPollTimersRef.current[video.id] = setTimeout(() => pollEventPlaybackUrl(video, attempt + 1), retryMs);
        return;
      }
      throw new Error(message || 'AVMSG conversion failed');
    } catch (e) {
      console.error('Failed to get play URL:', e);
      const detail = e?.response?.data?.detail || e?.response?.data?.message || e.message || 'Failed to prepare event video';
      setSelectedVideo(prev => (prev ? { ...prev, playback_status: 'failed', playback_message: detail } : prev));
      toast.error(detail);
    }
  }, [api]);

  const handleVideoClick = async (video) => {
    const filename = `${video.video_filename || video.filename || video.s3_key || ''}`.toLowerCase();
    const isSnapshot = video.media_kind === 'snapshot' || /\.(jpe?g|png|webp)$/.test(filename);
    if (isSnapshot) {
      toast.info('Snapshot images are used as video thumbnails');
      return;
    }
    // Normalise for VideoPlayerModal.handleDownload so the download
    // button fetches a fresh attachment-presigned URL keyed by
    // ``video_id`` and saves the file with its original device filename.
    const normalised = {
      ...video,
      video_id: video.id,
      filename: video.video_filename || video.filename,
    };
    setSelectedVideo(normalised);
    setVideoUrl(null);
    setPlayerOpen(true);

    if (video.s3_key) {
      if (playbackPollTimersRef.current[video.id]) clearTimeout(playbackPollTimersRef.current[video.id]);
      await pollEventPlaybackUrl(video, 0);
    }
  };

  const clearFilters = () => {
    setSelectedDeviceIds([]);
    setSelectedEventType('all');
    setSelectedChannel('all');
    setSelectedDate(null);
    setTimeFrom('');
    setTimeTo('');
    setDeepLinkFilterActive(false);
  };

  const clearTimeFilter = () => {
    setTimeFrom(DEFAULT_TIME_FROM);
    setTimeTo(DEFAULT_TIME_TO);
  };

  const handleCalendarDaySelect = (day, inMonth) => {
    if (!inMonth) return;
    setSelectedDate(day);
    setTimeFrom(DEFAULT_TIME_FROM);
    setTimeTo(DEFAULT_TIME_TO);
  };

  const getEventBadgeColor = (eventType) => {
    const t = String(eventType);
    const colors = {
      '42': 'bg-red-500', '41': 'bg-amber-500', '39': 'bg-orange-500',
      '36': 'bg-purple-500', '37': 'bg-pink-500', '38': 'bg-yellow-600',
      '50': 'bg-red-600', '52': 'bg-red-700', '53': 'bg-blue-500',
      '54': 'bg-blue-600', '57': 'bg-red-500', '58': 'bg-yellow-500',
    };
    return colors[t] || 'bg-slate-500';
  };

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(skip / limit) + 1;
  const hasCustomTimeFilter = selectedDate && (timeFrom !== DEFAULT_TIME_FROM || timeTo !== DEFAULT_TIME_TO);

  return (
    <div className="space-y-4" data-testid="event-videos-tab">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Video className="w-5 h-5 text-foreground" />
            <h2 className="text-xl font-semibold">Event Videos</h2>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Browse and playback recorded event videos from your fleet</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={fetchEvents} variant="outline" size="sm" className="gap-1" data-testid="ev-refresh-header">
            <RefreshCw className="w-3 h-3" /> Refresh
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        {/* Left Sidebar */}
        <aside className="space-y-3 rounded-lg border bg-card p-3 lg:w-80 lg:flex-none" data-testid="event-videos-filter-panel">
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm"><HardDrive className="h-4 w-4" /> Device</Label>
            <Popover open={deviceDropdownOpen} onOpenChange={setDeviceDropdownOpen}>
              <div className="flex gap-2">
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-8 min-w-0 flex-1 justify-between px-3 text-left font-normal dark:bg-[#172031]"
                    data-testid="ev-device-select"
                  >
                    {selectedDeviceIds.length > 0 ? (
                      <span className="flex min-w-0 items-center gap-2">
                        <span className={`h-2 w-2 shrink-0 rounded-full ${allDevicesSelected ? 'bg-emerald-500' : 'bg-blue-500'}`} />
                        <span className="min-w-0 truncate">{selectionLabel}</span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Select devices</span>
                    )}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                {selectedDeviceIds.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    onClick={clearEventDeviceSelection}
                    title="Clear selected devices"
                    aria-label="Clear selected devices"
                    data-testid="ev-clear-selected-device-button"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <PopoverContent className="z-[10000] w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={allDevicesSelected}
                  onClick={toggleAllDevices}
                  className="flex w-full items-center gap-2 border-b px-3 py-2 text-left text-xs font-semibold text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                  data-testid="ev-device-all-option"
                >
                  <span className={`flex h-3.5 w-3.5 items-center justify-center rounded-sm border text-[10px] ${allDevicesSelected ? 'border-blue-600 bg-blue-600 text-white' : 'border-muted-foreground/40'}`}>{allDevicesSelected ? '✓' : ''}</span>
                  All Devices
                </button>
                <div className="border-b bg-popover p-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      ref={deviceSearchInputRef}
                      value={deviceSearchQuery}
                      onChange={(e) => setDeviceSearchQuery(e.target.value)}
                      placeholder="Search by name or IMEI..."
                      className="h-8 pl-7 pr-7 text-xs"
                      data-testid="ev-device-search"
                    />
                    {deviceSearchQuery && (
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeviceSearchQuery(''); deviceSearchInputRef.current?.focus(); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        data-testid="ev-device-search-clear"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="mt-1 text-[10px] text-muted-foreground" data-testid="ev-device-search-count">
                    {filteredDevices.length}/{eventDevices.length} devices
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto p-1" data-testid="ev-device-search-results">
                  {filteredDevices.length === 0 ? (
                    <div className="px-3 py-3 text-center text-xs text-muted-foreground" data-testid="ev-device-search-empty">
                      No devices match your search
                    </div>
                  ) : filteredDevices.map(d => (
                    <button
                      key={d.id}
                      type="button"
                      role="checkbox"
                      aria-checked={selectedDeviceSet.has(d.id)}
                      onClick={() => toggleEventDevice(d.id)}
                      className={`flex w-full min-w-0 items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground ${selectedDeviceSet.has(d.id) ? 'bg-accent text-accent-foreground' : ''}`}
                      data-testid={`ev-device-option-${d.id}`}
                    >
                      <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border text-[10px] ${selectedDeviceSet.has(d.id) ? 'border-blue-600 bg-blue-600 text-white' : 'border-muted-foreground/40'}`}>{selectedDeviceSet.has(d.id) ? '✓' : ''}</span>
                      <span className={`h-2 w-2 shrink-0 rounded-full ${d.is_online ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                      <span className="min-w-0 truncate">{deviceDisplayName(d)}</span>
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <div className="rounded-md border bg-background p-2" data-testid="event-videos-calendar">
            <div className="mb-2 flex items-center justify-between">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1))}
                title="Previous month"
                data-testid="ev-calendar-prev-month"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-2 text-sm font-semibold">
                <span>{monthDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1))}
                title="Next month"
                data-testid="ev-calendar-next-month"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-muted-foreground">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day}>{day}</div>)}
            </div>
            <div className="mt-1 grid grid-cols-7 gap-1">
              {monthDays.map(day => {
                const key = dateKey(day);
                const meta = calendarDays[key];
                const inMonth = day.getMonth() === monthDate.getMonth();
                const isSelected = selectedKey === key;
                const colorClass = meta?.has_alarm ? 'bg-red-500 text-white hover:bg-red-600' : 'hover:bg-muted';
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleCalendarDaySelect(day, inMonth)}
                    disabled={!inMonth}
                    className={`relative h-8 rounded-full text-xs transition-colors ${inMonth ? colorClass : 'text-muted-foreground/40'} ${isSelected ? 'ring-2 ring-slate-900 ring-offset-2 dark:ring-white' : ''}`}
                    data-testid={`ev-calendar-day-${key}`}
                  >
                    {day.getDate()}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 flex items-center gap-3 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-red-500" />Alarm</span>
              <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full ring-2 ring-slate-900 dark:ring-white" />Selected</span>
            </div>
          </div>

          {selectedDeviceObj && (
            <div className="rounded-md border bg-slate-50 px-3 py-2 text-xs text-muted-foreground dark:bg-slate-900/40" data-testid="event-videos-device-timezone">
              Display timezone: <span className="font-medium text-foreground">{resolveTimezone({ device: selectedDeviceObj })}</span>
            </div>
          )}

          <div className="text-center py-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Videos</p>
              <p className="text-4xl font-bold text-[#2563eb]" data-testid="event-videos-total-count">{total}</p>
          </div>
        </aside>

        {/* Right Content */}
        <div className="flex-1 space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={selectedEventType} onValueChange={setSelectedEventType}>
              <SelectTrigger className="h-8 w-44" data-testid="ev-event-type-select">
                <SelectValue placeholder="All Events" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="ev-event-type-option-all">All Events</SelectItem>
                {[...eventTypes].sort((a, b) => String(a.label || '').localeCompare(String(b.label || ''))).map(t => (
                  <SelectItem key={t.value} value={t.value} data-testid={`ev-event-type-option-${testIdToken(t.value)}`}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedChannel} onValueChange={setSelectedChannel}>
              <SelectTrigger className="h-8 w-32" data-testid="ev-channel-select">
                <SelectValue placeholder="All CH" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="ev-channel-option-all">All Channels</SelectItem>
                {channelOptions.map(ch => (
                  <SelectItem key={ch.id} value={String(ch.id)} data-testid={`ev-channel-option-${testIdToken(ch.id)}`}>{ch.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedDate && (
              <div className="flex items-center gap-2 rounded-md border bg-background px-2 py-1 flex-wrap" data-testid="ev-time-of-day-filter">
                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                <Label className="text-xs text-muted-foreground whitespace-nowrap">Time</Label>
                <Select value={timePickerValue(timeFrom, DEFAULT_TIME_FROM)} onValueChange={(value) => setTimeFromPickerValue(setTimeFrom, value)}>
                  <SelectTrigger className="h-8 w-[104px] text-xs" data-testid="ev-time-from-input" aria-label="Event videos from time">
                    <SelectValue placeholder="From" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {TIME_OPTIONS.map(value => (
                      <SelectItem key={`from-${value}`} value={value} data-testid={`ev-time-from-option-${testIdToken(value)}`}>{value}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-xs text-muted-foreground">to</span>
                <Select value={timePickerValue(timeTo, DEFAULT_TIME_TO)} onValueChange={(value) => setTimeToPickerValue(setTimeTo, value)}>
                  <SelectTrigger className="h-8 w-[104px] text-xs" data-testid="ev-time-to-input" aria-label="Event videos to time">
                    <SelectValue placeholder="To" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {TIME_OPTIONS.map(value => (
                      <SelectItem key={`to-${value}`} value={value} data-testid={`ev-time-to-option-${testIdToken(value)}`}>{value}</SelectItem>
                    ))}
                    <SelectItem value="23:59" data-testid="ev-time-to-option-23-59">23:59</SelectItem>
                  </SelectContent>
                </Select>
                {hasCustomTimeFilter && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={clearTimeFilter}
                    title="Reset time filter to full day"
                    aria-label="Reset time filter to full day"
                    data-testid="ev-time-clear-button"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            )}

            <Button onClick={clearFilters} variant="outline" size="sm" className="gap-1 ml-auto" title="Reset all filters (device, date & time window)" data-testid="ev-clear-filters">
              <X className="w-3 h-3" /> Clear
            </Button>
          </div>

          {deepLinkFilterActive && (
            <div
              className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
              data-testid="ev-deeplink-filter-banner"
            >
              <Info className="mt-0.5 h-4 w-4 flex-none" />
              <span>
                Filtered from a notification to{' '}
                <span className="font-semibold" data-testid="ev-deeplink-device-name">
                  {events.length > 0
                    ? formatRecordDeviceLabel(events[0], devicesById)
                    : (deviceDisplayName(selectedDevices[0]) || 'the selected device')}
                </span>
                {selectedDate && (
                  <>
                    {' '}on{' '}
                    <span className="font-semibold" data-testid="ev-deeplink-date">
                      {selectedDate.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                  </>
                )}
                {timeFrom && timeTo && (
                  <>
                    {' '}between{' '}
                    <span className="font-semibold" data-testid="ev-deeplink-time-window">
                      {timeFrom.slice(0, 5)}–{timeTo.slice(0, 5)}
                    </span>
                  </>
                )}
                . To browse other devices,{' '}
                <button
                  type="button"
                  onClick={clearFilters}
                  className="font-semibold underline underline-offset-2 hover:no-underline"
                  data-testid="ev-deeplink-clear-link"
                >
                  Clear filters
                </button>.
              </span>
            </div>
          )}

          {selectedDeviceIds.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground" data-testid="event-videos-select-devices-empty-state">
              <HardDrive className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">Select devices to view event videos</p>
              <p className="text-sm">Choose All Devices or tick individual devices from the selector.</p>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-16" data-testid="event-videos-loading-state">
              <Loader2 className="w-8 h-8 animate-spin text-[#2563eb]" />
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground" data-testid="event-videos-empty-state">
              <Video className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No event videos found</p>
              <p className="text-sm">Try adjusting your filters or selecting a different date</p>
            </div>
          ) : (
            <>
              {/* Device-map lookup for timezone rendering on each card */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4" data-testid="event-videos-grid">
                {events.map((event) => {
                  const eventDevice = deviceById[event.device_id] || eventDevices.find(d => d.id === event.device_id) || event;
                  const evTz = resolveTimezone({ device: eventDevice });
                  const filename = `${event.video_filename || event.filename || event.s3_key || ''}`.toLowerCase();
                  const isSnapshot = event.media_kind === 'snapshot' || /\.(jpe?g|png|webp)$/.test(filename);
                  const isAvmsg = filename.endsWith('.avmsg');
                  const avmsgStatus = event.playback_status;
                  const labelRows = eventLabelRows(event);
                  const channelText = event.channel_label || channelLabelFor(eventDevice, event.channel);
                  return (
                  <Card
                    key={event.id}
                    className={`overflow-hidden transition-shadow group ${isSnapshot ? 'cursor-default opacity-80' : 'cursor-pointer hover:shadow-lg'}`}
                    onClick={() => handleVideoClick(event)}
                    data-testid={`event-card-${event.id}`}
                  >
                    <div className="aspect-video bg-slate-800 relative flex items-center justify-center">
                      <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-transparent" />
                      {event.thumbnail_url ? (
                        <>
                          <img
                            src={event.thumbnail_url}
                            alt=""
                            className="w-full h-full object-cover"
                            data-testid={`event-card-thumbnail-${event.id}`}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              const ph = e.currentTarget.nextElementSibling;
                              if (ph) ph.style.display = 'flex';
                            }}
                          />
                          <span className="hidden items-center justify-center" data-testid={`event-card-thumbnail-fallback-${event.id}`}>
                            <FileVideo className="w-12 h-12 text-slate-600" />
                          </span>
                        </>
                      ) : (
                        <FileVideo className="w-12 h-12 text-slate-600" />
                      )}
                      <div
                        className="absolute left-2 top-2 z-10 flex max-w-[calc(100%-5.75rem)] flex-col items-start gap-1"
                        title={eventLabelText(event)}
                        data-testid={`event-card-event-label-${event.id}`}
                      >
                        {labelRows.map((label, idx) => (
                          <Badge
                            key={`${event.id}-overlay-label-${label}-${idx}`}
                            className={`max-w-full truncate ${getEventBadgeColor(idx === 0 ? event.event_type : label)} text-xs text-white shadow-sm`}
                            data-testid={`event-card-event-label-line-${event.id}-${idx}`}
                          >
                            {label}
                          </Badge>
                        ))}
                      </div>
                      {event.has_multiple_events && (
                        <Badge className="absolute bottom-2 right-2 z-10 bg-amber-600 text-white text-[10px] shadow-md" data-testid={`event-card-multiple-events-badge-${event.id}`}>
                          Multiple Events
                        </Badge>
                      )}
                      <Badge className="absolute top-2 right-2 z-10 bg-[#2563eb] text-white text-xs" data-testid={`event-card-channel-${event.id}`}>
                        {channelText}
                      </Badge>
                      {event.status === 'pending' && (
                        <Badge className="absolute bottom-2 left-2 bg-yellow-500 text-white text-xs">
                          Uploading...
                        </Badge>
                      )}
                      {isAvmsg && event.status !== 'pending' && (
                        <Badge
                          className={`absolute bottom-2 left-2 text-white text-xs ${avmsgStatus === 'ready' ? 'bg-emerald-600' : avmsgStatus === 'failed' ? 'bg-red-600' : 'bg-amber-500'}`}
                          data-testid={`event-card-avmsg-status-${event.id}`}
                        >
                          {avmsgStatus === 'ready' ? 'AVMSG ready' : avmsgStatus === 'failed' ? 'AVMSG failed' : avmsgStatus === 'queued' ? 'Queued AVMSG' : avmsgStatus === 'converting' ? 'Converting AVMSG' : 'Needs conversion'}
                        </Badge>
                      )}
                      {event.status === 'uploaded' && (
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                            <Play className="w-6 h-6 text-[#2563eb] ml-1" />
                          </div>
                        </div>
                      )}
                    </div>
                    <CardContent className="p-3 space-y-2">
                      <h4 className="space-y-0.5 text-sm font-semibold leading-snug" data-testid={`event-card-event-description-${event.id}`}>
                        {labelRows.map((label, idx) => (
                          <span
                            key={`${event.id}-label-${label}-${idx}`}
                            className="block whitespace-normal break-words"
                            title={label}
                            data-testid={`event-card-event-description-line-${event.id}-${idx}`}
                          >
                            {label}{!event.has_multiple_events && idx === 0 ? ` - ${channelText}` : ''}
                          </span>
                        ))}
                      </h4>
                      {(() => {
                        const label = formatRecordDeviceLabel(event, devicesById);
                        return label && label !== '-' ? (
                          <p className="text-xs text-muted-foreground truncate" data-testid={`event-card-device-${event.id}`}>{label}</p>
                        ) : null;
                      })()}
                      <div className="space-y-1 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1.5" title={evTz ? `Device timezone: ${evTz}` : ''}>
                          <CalendarIcon className="w-3 h-3" />
                          <span data-testid={`event-card-time-${event.id}`}>{eventDisplayTime(event, eventDevice)}</span>
                        </div>
                        {event.file_size_display && (
                          <div className="flex items-center gap-1.5">
                            <HardDrive className="w-3 h-3" />
                            <span data-testid={`event-card-size-${event.id}`}>{event.file_size_display}</span>
                          </div>
                        )}
                        {event.speed != null && (
                          <div className="flex items-center gap-1.5 text-[#2563eb]">
                            <Gauge className="w-3 h-3" />
                            <span data-testid={`event-card-speed-${event.id}`}>Speed: {formatSpeed(event.speed)}</span>
                          </div>
                        )}
                        {hasEventLocation(event) && (
                          <a
                            href={`https://www.google.com/maps?q=${Number(event.latitude)},${Number(event.longitude)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-1.5 text-[#16a34a] hover:underline"
                            title={`Open in Google Maps — ${Number(event.latitude).toFixed(6)}, ${Number(event.longitude).toFixed(6)}`}
                            data-testid={`event-card-location-${event.id}`}
                          >
                            <MapPin className="w-3 h-3" />
                            <span className="truncate">{Number(event.latitude).toFixed(5)}, {Number(event.longitude).toFixed(5)}</span>
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 pt-4">
                  <Button
                    variant="outline" size="sm"
                    disabled={currentPage <= 1}
                    onClick={() => setSkip(Math.max(0, skip - limit))}
                    data-testid="event-videos-previous-page-button"
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline" size="sm"
                    disabled={currentPage >= totalPages}
                    onClick={() => setSkip(skip + limit)}
                    data-testid="event-videos-next-page-button"
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <VideoPlayerModal
        open={playerOpen}
        onClose={setPlayerOpen}
        video={selectedVideo}
        videoUrl={videoUrl}
        device={selectedVideo ? devices.find(d => d.id === selectedVideo.device_id) : null}
      />

      {/* Multi-clip chooser: one event, several camera angles */}
      <Dialog open={!!clipChoices} onOpenChange={(o) => { if (!o) setClipChoices(null); }}>
        <DialogContent className="max-w-sm" data-testid="event-clip-chooser">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="w-4 h-4 text-blue-500" />
              This event has {clipChoices?.length} videos
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {clipChoices?.map(clip => (
              <Button
                key={clip.id}
                variant="outline"
                className="w-full justify-start gap-2 h-11"
                data-testid={`event-clip-choice-${clip.id}`}
                onClick={() => { setClipChoices(null); handleVideoClick(clip); }}
              >
                <Play className="w-4 h-4 text-blue-500 flex-shrink-0" />
                <span className="font-semibold">{clip.channel_label || (clip.channel != null ? `CH${clip.channel}` : 'Channel')}</span>
                <span className="text-xs text-muted-foreground truncate">{stripEventCodeSuffix(clip.event_label_display || clip.event_label) || clip.event_type}</span>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
